// src/modules/nlp/TokenizationLab.jsx
// Tokenization Lab — Word · Sentence · N-gram · BPE/Subword · Stemming · Live Playground
// Tabs: input | word | sentence | ngram | subword | stemming | playground | concepts

import React, { useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, ScatterChart, Scatter } from 'recharts';
import { tokenizeWords, tokenizeSentences, removeStopWords, tokenizeNgrams, stem, STOP_WORDS, SAMPLE_TEXTS, textStats } from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--cyan)';
const P = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399'];
const TABS = ['input','word','sentence','ngram','subword','stemming','playground','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const SliderRow = ({label,min,max,value,onChange,color=ACCENT})=>(
  <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
    <span style={{fontSize:'10px',color:'var(--t3)',minWidth:'120px'}}>{label}</span>
    <input type="range" min={min} max={max} value={value} onChange={e=>onChange(+e.target.value)} style={{flex:1,accentColor:color}}/>
    <span style={{fontSize:'11px',color,fontWeight:700,minWidth:'28px',textAlign:'right'}}>{value}</span>
  </div>
);

export default function TokenizationLab() {
  const [tab, setTab]       = useState('input');
  const [text, setText]     = useState(SAMPLE_TEXTS.tech);
  const [showStop, setShowStop] = useState(true);
  const [ngramN, setNgramN] = useState(2);
  const [stemInput, setStemInput] = useState('running better loves happiness organizations quickly computational');
  const [playInput, setPlayInput] = useState('The quick brown fox jumps over the lazy dog');
  const [playMode, setPlayMode]   = useState('word');
  const [playN, setPlayN]         = useState(2);
  const [bpeSteps, setBpeSteps]   = useState(0);

  // Word tokens
  const allWords = tokenizeWords(text);
  const display  = showStop ? allWords : removeStopWords(allWords);
  const wordFreq = {};
  allWords.forEach(w=>{wordFreq[w]=(wordFreq[w]||0)+1;});
  const topWords = Object.entries(wordFreq).sort((a,b)=>b[1]-a[1]).slice(0,20).map(([word,count])=>({word,count}));
  const contentTop = Object.entries({...wordFreq}).filter(([w])=>!STOP_WORDS.has(w)&&w.length>2).sort((a,b)=>b[1]-a[1]).slice(0,20).map(([word,count])=>({word,count}));

  // Sentence tokens
  const sentences = tokenizeSentences(text);
  const sentLens  = sentences.map(s=>tokenizeWords(s).length);

  // N-grams
  const noStop  = removeStopWords(allWords).filter(w=>w.length>2);
  const ngrams  = tokenizeNgrams(noStop, ngramN);
  const ngFreq  = {};
  ngrams.forEach(ng=>{ngFreq[ng]=(ngFreq[ng]||0)+1;});
  const topNg   = Object.entries(ngFreq).sort((a,b)=>b[1]-a[1]).slice(0,20).map(([gram,count])=>({gram,count}));

  // Stemming
  const stemWords = stemInput.trim().split(/\s+/).filter(Boolean);

  // BPE simulation
  const simulateBPE = (inputText, steps) => {
    const wordList = inputText.toLowerCase().split(/\s+/).slice(0,8).filter(Boolean);
    // Start: char-level + </w>
    let vocab = wordList.map(w => w.split('').join(' ') + ' </w>');
    const mergeHistory = [];

    for (let step = 0; step < steps; step++) {
      // Count pairs
      const pairFreq = {};
      vocab.forEach(word => {
        const parts = word.split(' ');
        for (let i=0; i<parts.length-1; i++) {
          const pair = parts[i]+' '+parts[i+1];
          pairFreq[pair] = (pairFreq[pair]||0)+1;
        }
      });
      const best = Object.entries(pairFreq).sort((a,b)=>b[1]-a[1])[0];
      if (!best || best[1] < 2) break;
      const [pair] = best;
      const [a,b]  = pair.split(' ');
      mergeHistory.push({step:step+1, merged:`${a}+${b}→${a+b}`, freq:best[1]});
      vocab = vocab.map(word => word.split(' ').reduce((acc,tok,i,arr)=>{
        if(i>0&&arr[i-1]===a&&tok===b&&acc[acc.length-1]===a){acc[acc.length-1]=a+b;}
        else acc.push(tok);
        return acc;
      },[]).join(' '));
    }
    return { vocab, mergeHistory };
  };

  const bpeResult = simulateBPE(text.split(' ').slice(0,8).join(' '), bpeSteps);

  // Playground tokenize
  const playTokenize = () => {
    const w = tokenizeWords(playInput);
    if (playMode === 'word')     return w;
    if (playMode === 'sentence') return tokenizeSentences(playInput);
    if (playMode === 'nostop')   return removeStopWords(w);
    if (playMode === 'stem')     return w.map(t=>stem(t));
    if (playMode === 'ngram')    return tokenizeNgrams(w, playN);
    if (playMode === 'char')     return playInput.split('');
    return w;
  };
  const playTokens = playTokenize();

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INPUT TEXT" accent={ACCENT}>
            <textarea value={text} onChange={e=>setText(e.target.value)} style={{width:'100%',minHeight:'160px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>setText(SAMPLE_TEXTS[key])} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key}</button>
              ))}
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[['Words',allWords.length,ACCENT],['Unique',new Set(allWords).size,'var(--violet)'],['Sentences',sentences.length,'var(--green)'],['Characters',text.length,'var(--gold)'],
              ['Avg word len',textStats(text).avgWordLen+'c','var(--orange)'],['Avg sent len',textStats(text).avgSentLen+'w','var(--cyan)'],['Lex diversity',textStats(text).lexDiv,'var(--violet)'],['Stopwords',allWords.filter(w=>STOP_WORDS.has(w)).length,'var(--t3)']
            ].map(([l,v,c])=>(
              <div key={l} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${c}22`,borderRadius:'3px',textAlign:'center'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{l.toUpperCase()}</div>
                <div style={{fontSize:'20px',fontWeight:700,fontFamily:'Syne',color:c}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WORD TOKENIZATION ── */}
      {tab==='word' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="WORD TOKEN STREAM" accent={ACCENT}>
            <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'12px',flexWrap:'wrap'}}>
              <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',color:'var(--t2)',cursor:'pointer'}}>
                <input type="checkbox" checked={showStop} onChange={e=>setShowStop(e.target.checked)} style={{accentColor:ACCENT}}/>
                Show stopwords
              </label>
              <Tag>{display.length} tokens</Tag>
              <Tag color="var(--violet)">{new Set(display).size} unique</Tag>
              {!showStop&&<Tag color="var(--green)">−{allWords.length-display.length} stopwords removed</Tag>}
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:'4px',maxHeight:'180px',overflowY:'auto',padding:'10px',background:'var(--deep)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
              {display.slice(0,300).map((w,i)=>{
                const isStop=STOP_WORDS.has(w);
                return <span key={i} style={{padding:'3px 8px',background:isStop?'var(--raised)':ACCENT+'20',border:`1px solid ${isStop?'var(--b0)':ACCENT+'44'}`,color:isStop?'var(--t3)':ACCENT,fontSize:'11px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}</span>;
              })}
              {display.length>300&&<span style={{color:'var(--t3)',fontSize:'10px',padding:'3px 8px'}}>…+{display.length-300} more</span>}
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="ALL WORDS FREQUENCY (top 20)" accent={ACCENT}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={topWords}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:8}} angle={-30} textAnchor="end" height={55} interval={0}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                  <Tooltip/>
                  <Bar dataKey="count" radius={[2,2,0,0]}>{topWords.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="CONTENT WORDS ONLY (no stopwords, top 20)" accent="var(--green)">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={contentTop} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                  <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}}/>
                  <YAxis type="category" dataKey="word" stroke="var(--t3)" tick={{fontSize:9}} width={80}/>
                  <Tooltip/>
                  <Bar dataKey="count" radius={[0,2,2,0]}>{contentTop.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <Panel title="TOKEN LENGTH DISTRIBUTION" accent="var(--gold)">
            {(()=>{
              const bins=[1,2,3,4,5,6,7,8,9,10,15,20];
              const data=bins.slice(0,-1).map((lo,i)=>({len:`${lo}-${bins[i+1]-1}`,count:allWords.filter(w=>w.length>=lo&&w.length<bins[i+1]).length}));
              return (
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="len" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Bar dataKey="count" fill="var(--gold)" opacity={.8} radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </Panel>

          <InfoBox color={ACCENT} icon="◈">
            Word tokenization: split text into individual words using regex boundary <code>\b[a-z][a-z']*\b</code>. Challenges: contractions (don't→don,t), hyphenated words (state-of-the-art), abbreviations (U.S.A.), URLs. Different languages need different rules — Chinese has no spaces, Arabic is highly inflected.
          </InfoBox>
        </div>
      )}

      {/* ── SENTENCE TOKENIZATION ── */}
      {tab==='sentence' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title={`SENTENCE TOKENS — ${sentences.length} detected`} accent="var(--green)">
            <div style={{maxHeight:'320px',overflowY:'auto'}}>
              {sentences.map((s,i)=>{
                const wc=tokenizeWords(s).length;
                const complexity=wc>20?'complex':wc>10?'medium':'simple';
                const cc={'complex':'var(--red)','medium':'var(--gold)','simple':'var(--green)'};
                return (
                  <div key={i} style={{display:'flex',gap:'10px',padding:'10px 12px',background:'var(--raised)',borderRadius:'3px',marginBottom:'5px',border:'1px solid var(--b0)',alignItems:'flex-start'}}>
                    <span style={{fontSize:'10px',color:'var(--green)',fontWeight:700,fontFamily:'JetBrains Mono',flexShrink:0,minWidth:'24px'}}>#{i+1}</span>
                    <span style={{fontSize:'11px',color:'var(--t2)',lineHeight:1.65,flex:1}}>{s}</span>
                    <div style={{display:'flex',flexDirection:'column',gap:'3px',flexShrink:0,alignItems:'flex-end'}}>
                      <Tag color="var(--cyan)">{wc}w</Tag>
                      <Tag color={cc[complexity]}>{complexity}</Tag>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="SENTENCE LENGTH DISTRIBUTION (words)" accent="var(--green)">
              {(()=>{
                const bins=[0,5,10,15,20,30,50,100];
                const labels=['1-5','6-10','11-15','16-20','21-30','31-50','50+'];
                const data=labels.map((l,i)=>({l,count:sentLens.filter(n=>n>bins[i]&&n<=bins[i+1]).length}));
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="l" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="count" fill="var(--green)" opacity={.8} radius={[2,2,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </Panel>
            <Panel title="SENTENCE STATS" accent="var(--green)">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                {[
                  ['Count',sentences.length,'var(--green)'],
                  ['Avg length',+(sentLens.reduce((a,b)=>a+b,0)/(sentLens.length||1)).toFixed(1)+'w','var(--cyan)'],
                  ['Shortest',Math.min(...sentLens)+'w','var(--gold)'],
                  ['Longest',Math.max(...sentLens)+'w','var(--red)'],
                  ['< 10w',sentLens.filter(l=>l<10).length,'var(--green)'],
                  ['> 25w',sentLens.filter(l=>l>25).length,'var(--orange)'],
                ].map(([l,v,c])=>(
                  <div key={l} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${c}22`,borderRadius:'3px',textAlign:'center'}}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>{l}</div>
                    <div style={{fontSize:'18px',fontWeight:700,color:c,fontFamily:'Syne'}}>{v}</div>
                  </div>
                ))}
              </div>
              <InfoBox color="var(--green)" icon="◈" style={{marginTop:'10px'}}>
                Sentence boundary detection is harder than it looks. "Dr. Smith arrived at 9 p.m. on Jan. 5." — periods after abbreviations aren't sentence boundaries. SpaCy and NLTK use rule-based + ML approaches. Punkt tokenizer uses unsupervised learning to detect abbreviations.
              </InfoBox>
            </Panel>
          </div>
        </div>
      )}

      {/* ── N-GRAM ── */}
      {tab==='ngram' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="N-GRAM FREQUENCY ANALYSIS" accent="var(--gold)">
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'14px',flexWrap:'wrap'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>N =</span>
              {[1,2,3,4,5].map(v=>(
                <button key={v} onClick={()=>setNgramN(v)} style={{padding:'5px 14px',border:`1px solid ${ngramN===v?'var(--gold)':'var(--b0)'}`,background:ngramN===v?'var(--ggold)':'var(--raised)',color:ngramN===v?'var(--gold)':'var(--t2)',fontSize:'11px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:ngramN===v?700:400}}>{v}</button>
              ))}
              <Tag color="var(--gold)">{ngrams.length} {['','uni','bi','tri','4','5'][ngramN]}grams</Tag>
              <Tag color="var(--t3)">{Object.keys(ngFreq).length} unique</Tag>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topNg.slice(0,15)}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="gram" stroke="var(--t3)" tick={{fontSize:8}} angle={-30} textAnchor="end" height={60} interval={0}/>
                <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                <Tooltip/>
                <Bar dataKey="count" radius={[2,2,0,0]}>{topNg.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="TOP N-GRAMS TABLE" accent="var(--gold)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'6px'}}>
              {topNg.map(({gram,count},i)=>(
                <div key={gram} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 10px',background:'var(--raised)',borderRadius:'2px',border:'1px solid var(--b0)'}}>
                  <span style={{fontSize:'11px',color:'var(--t2)',fontFamily:'JetBrains Mono',flex:1}}><span style={{color:'var(--t3)',marginRight:'6px'}}>#{i+1}</span>{gram}</span>
                  <Tag color="var(--gold)">{count}×</Tag>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="N-GRAM THEORY" accent="var(--gold)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                ['Unigrams (N=1)','var(--cyan)','Each word independently. Bag-of-Words model. Ignores word order entirely. Good for topic classification. TF-IDF uses unigrams. Very sparse for large vocabulary.'],
                ['Bigrams (N=2)','var(--gold)','Adjacent word pairs: "machine learning", "deep learning". Captures local context. Standard in language models, spell checking, text prediction. Manageable sparsity.'],
                ['Trigrams (N=3)','var(--green)','Three-word sequences. Better context but exponentially more sparse. C(V,3) possible trigrams for vocab V. Kneser-Ney smoothing essential. Used in speech recognition.'],
                ['Higher N (4-5)','var(--violet)','Very sparse, needs huge corpus. Google N-gram corpus has 5-grams from 1 trillion words. Rarely used beyond trigrams in practice. Neural LMs made high-N grams obsolete.'],
                ['Character N-grams','var(--orange)','N-grams over characters instead of words. Handles OOV words, typos, multilingual text. FastText uses char 3-6 grams. Byte n-grams work across any language.'],
                ['Skip-grams','var(--red)','N-grams with gaps: "the _ fox" matches "the quick fox". More robust to insertions. Used in Word2Vec training objective. Improves recall in information retrieval.'],
              ].map(([title,color,desc])=>(
                <div key={title} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'6px'}}>{title}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── SUBWORD / BPE ── */}
      {tab==='subword' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="BYTE-PAIR ENCODING (BPE) — LIVE SIMULATION" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              BPE starts with character-level tokens and iteratively merges the most frequent adjacent pair. Step through the algorithm to see how subword units emerge from raw characters.
            </div>
            <SliderRow label={`Merge steps: ${bpeSteps}`} min={0} max={15} value={bpeSteps} onChange={setBpeSteps} color="var(--violet)"/>

            <div style={{marginTop:'14px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>TOKENIZED WORDS (first 8)</div>
              {bpeResult.vocab.map((tok,i)=>(
                <div key={i} style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'6px',alignItems:'center'}}>
                  <span style={{fontSize:'10px',color:'var(--t3)',width:'22px',textAlign:'right'}}>#{i+1}</span>
                  {tok.split(' ').map((ch,j)=>(
                    <span key={j} style={{padding:'3px 8px',background:ch==='</w>'?'var(--violet)20':'var(--gcyan)',border:`1px solid ${ch==='</w>'?'var(--violet)44':'var(--cyan)44'}`,color:ch==='</w>'?'var(--violet)':'var(--cyan)',fontSize:'11px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{ch}</span>
                  ))}
                </div>
              ))}
            </div>

            {bpeResult.mergeHistory.length > 0 && (
              <div style={{marginTop:'14px'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>MERGE HISTORY</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                  {bpeResult.mergeHistory.map(({step,merged,freq})=>(
                    <div key={step} style={{padding:'5px 10px',background:'var(--raised)',border:'1px solid var(--violet)33',borderRadius:'3px',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                      <span style={{color:'var(--t3)',marginRight:'4px'}}>#{step}</span>
                      <span style={{color:'var(--violet)',fontWeight:700}}>{merged}</span>
                      <span style={{color:'var(--t3)',marginLeft:'4px'}}>×{freq}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
            {[
              ['BPE (GPT-2/3/4)','var(--violet)','Start: characters. Iteratively merge most frequent pair. GPT-4 uses cl100k_base with ~100K tokens. Handles any word — unknown words split into known subwords. "unfamiliarization" → ["un","familiar","ization"].'],
              ['WordPiece (BERT)','var(--cyan)','## prefix marks continuation: "playing"→["play","##ing"]. Trains by maximizing LM likelihood rather than frequency. Google\'s BERT vocabulary: 30,522 tokens. Used in BERT, DistilBERT, ELECTRA.'],
              ['SentencePiece (LLaMA)','var(--green)','Treats text as raw Unicode stream — no pre-tokenization. Works on all languages without spaces (Japanese, Chinese, Thai). LLaMA uses SentencePiece with 32K vocab. ▁ marks word start.'],
              ['Unigram LM','var(--gold)','Starts with large vocab, prunes tokens with lowest loss impact. More principled than BPE — directly optimizes a language model probability. XLNet, ALBERT use this. More mathematically principled than BPE.'],
              ['Byte-level BPE','var(--orange)','Operates on raw bytes (0-255) not Unicode characters. Never produces UNK tokens — any byte sequence representable. GPT-2 uses byte-level BPE. Handles emojis, special chars, code naturally.'],
              ['Tiktoken (GPT-4)','var(--red)','OpenAI\'s fast BPE tokenizer, 10-100x faster than HuggingFace. cl100k_base vocab. "ChatGPT" = 1 token, "Transformer" = 1 token, "transformers" = 1 token. Test at platform.openai.com/tokenizer.'],
            ].map(([title,color,desc])=>(
              <div key={title} style={{padding:'13px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
              </div>
            ))}
          </div>

          {/* Vocabulary comparison */}
          <Panel title="VOCABULARY SIZE COMPARISON" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'8px'}}>
              {[
                ['Character','var(--t3)','26-256','Any text'],
                ['BPE-small','var(--cyan)','8K-16K','Small models'],
                ['BERT','var(--gold)','30K','Classic NLP'],
                ['GPT-2','var(--green)','50K','Mid models'],
                ['GPT-4','var(--violet)','100K','Modern LLMs'],
              ].map(([name,color,size,use])=>(
                <div key={name} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}44`,borderRadius:'4px',textAlign:'center'}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'4px'}}>{name}</div>
                  <div style={{fontSize:'16px',fontWeight:700,fontFamily:'Syne',color,marginBottom:'4px'}}>{size}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>{use}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── STEMMING ── */}
      {tab==='stemming' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="STEMMING vs LEMMATIZATION — COMPARISON" accent="var(--orange)">
            <div style={{marginBottom:'12px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>WORDS TO ANALYZE (space-separated)</div>
              <input value={stemInput} onChange={e=>setStemInput(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',boxSizing:'border-box'}}/>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                <thead>
                  <tr style={{background:'var(--deep)'}}>
                    {['Original','Porter Stem','Change?','Lemma (approx)','POS hint'].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'var(--t3)',fontSize:'9px',letterSpacing:'.08em',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stemWords.map((w,i)=>{
                    const s=stem(w);
                    const changed=s!==w.toLowerCase();
                    // Simple lemma approximation
                    const lemmaMap={'running':'run','runs':'run','ran':'run','better':'good','best':'good','worse':'bad','worst':'bad','went':'go','goes':'go','gone':'go','was':'be','were':'be','been':'be','are':'be','is':'be','am':'be','having':'have','has':'have','had':'have','doing':'do','does':'do','did':'do','loves':'love','loved':'love','loving':'love','quickly':'quick','happiness':'happy','computational':'compute','organizations':'organization'};
                    const lemma=lemmaMap[w.toLowerCase()]||w.toLowerCase().replace(/ing$|ed$|ly$|ness$|tion$|ment$/,'');
                    const posMap={'running':'verb','loves':'verb','quickly':'adverb','happiness':'noun','computational':'adjective','organizations':'noun','better':'adjective','ran':'verb'};
                    const pos=posMap[w.toLowerCase()]||'?';
                    return (
                      <tr key={i} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.05)'}}>
                        <td style={{padding:'8px 12px',color:'var(--t1)',fontFamily:'JetBrains Mono'}}>{w}</td>
                        <td style={{padding:'8px 12px',color:changed?'var(--orange)':'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:changed?700:400}}>{s}</td>
                        <td style={{padding:'8px 12px'}}>{changed?<Tag color="var(--orange)">changed</Tag>:<Tag color="var(--t3)">same</Tag>}</td>
                        <td style={{padding:'8px 12px',color:lemma!==w.toLowerCase()?'var(--cyan)':'var(--t3)',fontFamily:'JetBrains Mono'}}>{lemma}</td>
                        <td style={{padding:'8px 12px',color:'var(--t3)',fontSize:'10px'}}>{pos}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {[
              ['Porter Stemmer (1980)','var(--orange)','5 algorithmic steps: suffix stripping rules. "running"→"run", "happiness"→"happi", "generalization"→"general". Fast O(n). Over-stems: "universe"→"univers". Under-stems: "data/datum" not conflated. Still widely used.'],
              ['Snowball Stemmer','var(--gold)','Martin Porter\'s improved framework. Supports 15+ languages: English, French, German, Spanish, etc. More conservative than Porter. Standard in Elasticsearch, Solr, Lucene. Better precision vs Porter.'],
              ['Lemmatization','var(--cyan)','Dictionary-based: finds canonical root form. Uses POS tags for accuracy: "better" (adj) → "good", not "better" (v) → "better". Requires WordNet or similar resource. Slower but linguistically correct.'],
              ['WordNet Lemmatizer','var(--green)','NLTK WordNetLemmatizer. Uses Princeton WordNet: 155K words, 175K sense pairs. "geese"→"goose", "mice"→"mouse", "went"→"go". Needs POS tag input for best results. Gold standard.'],
              ['spaCy Lemmatizer','var(--violet)','Rule-based + lookup table hybrid. Language-specific rules + exception lists. "n\'t"→"not", "\'re"→"be". Much faster than WordNet. 50+ language models available. Part of spaCy pipeline.'],
              ['When to Use Which','var(--red)','Stemming: IR/search engines (speed matters, recall > precision). Lemmatization: when linguistic accuracy matters (QA, translation, morphological analysis). Neither: modern BERT/transformers handle inflection implicitly via subword tokens.'],
            ].map(([title,color,desc])=>(
              <div key={title} style={{padding:'13px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PLAYGROUND ── */}
      {tab==='playground' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LIVE TOKENIZATION PLAYGROUND" accent={ACCENT}>
            <div style={{marginBottom:'12px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>INPUT TEXT</div>
              <textarea value={playInput} onChange={e=>setPlayInput(e.target.value)} style={{width:'100%',minHeight:'80px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'10px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            </div>

            <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',marginBottom:'14px'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>MODE:</span>
              {[['word','Word'],['sentence','Sentence'],['nostop','No Stopwords'],['stem','Stemmed'],['ngram','N-gram'],['char','Character']].map(([m,l])=>(
                <button key={m} onClick={()=>setPlayMode(m)} style={{padding:'5px 12px',border:`1px solid ${playMode===m?ACCENT:'var(--b0)'}`,background:playMode===m?ACCENT+'18':'var(--raised)',color:playMode===m?ACCENT:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:playMode===m?700:400}}>{l}</button>
              ))}
              {playMode==='ngram'&&(
                <div style={{display:'flex',gap:'4px',alignItems:'center'}}>
                  <span style={{fontSize:'10px',color:'var(--t3)'}}>N=</span>
                  {[2,3,4].map(v=><button key={v} onClick={()=>setPlayN(v)} style={{padding:'4px 10px',border:`1px solid ${playN===v?ACCENT:'var(--b0)'}`,background:playN===v?ACCENT+'18':'var(--raised)',color:playN===v?ACCENT:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:playN===v?700:400}}>{v}</button>)}
                </div>
              )}
            </div>

            <div style={{padding:'6px 10px',background:'var(--deep)',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'10px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <Tag>{playTokens.length} tokens</Tag>
              <Tag color="var(--violet)">{new Set(playTokens).size} unique</Tag>
              {playMode==='word'&&<Tag color="var(--green)">avg len: {+(playTokens.reduce((s,w)=>s+w.length,0)/(playTokens.length||1)).toFixed(1)}c</Tag>}
            </div>

            <div style={{display:'flex',flexWrap:'wrap',gap:'5px',minHeight:'60px',padding:'10px',background:'var(--deep)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
              {playTokens.map((tok,i)=>{
                const colors=[ACCENT,'var(--green)','var(--violet)','var(--gold)','var(--orange)'];
                const c = playMode==='ngram' ? colors[i%colors.length] : (playMode==='char'?'var(--t2)':ACCENT);
                return <span key={i} style={{padding:'4px 10px',background:c+'18',border:`1px solid ${c}44`,color:c,fontSize:'12px',borderRadius:'3px',fontFamily:'JetBrains Mono',cursor:'default'}} title={`Token #${i+1}: "${tok}" (${tok.length} chars)`}>{tok}</span>;
              })}
              {playTokens.length===0&&<span style={{color:'var(--t3)',fontSize:'11px'}}>Type something above…</span>}
            </div>
          </Panel>

          <Panel title="TOKEN ANALYSIS" accent={ACCENT}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              {/* Token frequency */}
              {(()=>{
                const freq={};playTokens.forEach(t=>{freq[t]=(freq[t]||0)+1;});
                const data=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,12).map(([tok,count])=>({tok:tok.length>12?tok.slice(0,12)+'…':tok,count}));
                return (
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>TOKEN FREQUENCY</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="tok" stroke="var(--t3)" tick={{fontSize:8}} angle={-25} textAnchor="end" height={50} interval={0}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                        <Tooltip/>
                        <Bar dataKey="count" radius={[2,2,0,0]}>{data.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Character frequency */}
              {(()=>{
                const freq={};playInput.toLowerCase().replace(/[^a-z]/g,'').split('').forEach(c=>{freq[c]=(freq[c]||0)+1;});
                const data=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([char,count])=>({char,count}));
                return (
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>CHARACTER FREQUENCY</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="char" stroke="var(--t3)" tick={{fontSize:10}}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                        <Tooltip/>
                        <Bar dataKey="count" fill="var(--violet)" opacity={.8} radius={[2,2,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </div>
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Tokenization in NLP Pipeline',color:ACCENT,desc:'Tokenization is always the first step in any NLP pipeline. Raw text → tokens → embeddings → model. The quality of tokenization directly affects everything downstream. A bad tokenizer can create information bottlenecks that no amount of model sophistication can fix.'},
              {title:'Zipf\'s Law',color:'var(--gold)',desc:'In any natural language corpus, the frequency of a word is inversely proportional to its rank. The most common word is ~2× more frequent than the 2nd, ~3× more than the 3rd. Consequence: most vocabulary items are very rare, most tokens come from a small set of common words. Motivates stopword removal.'},
              {title:'The Vocabulary Problem',color:'var(--violet)',desc:'Word-level tokenization: ~500K-1M distinct English words. Character-level: only ~100 tokens but long sequences. Subword is the sweet spot: 30K-100K vocab, handles OOV, reasonable sequence length. BPE/WordPiece balance coverage vs sequence length.'},
              {title:'Morphological Analysis',color:'var(--green)',desc:'Languages vary enormously in morphological complexity. English: "run/runs/ran/running" (4 forms). Turkish "evlerinizden" = "from your houses" (1 word, many morphemes). Arabic: trilateral root system. Subword tokenization handles morphology better than fixed vocabularies.'},
              {title:'Tokenization Challenges',color:'var(--orange)',desc:'Clitics: "don\'t"→"do"+"n\'t". Compounds: "ice cream" (1 or 2 tokens?). Named entities: "New York City" (3 tokens). Code switching: multilingual text. Emojis and special Unicode. Normalization: case, punctuation, accents. No universal solution.'},
              {title:'Tokenization & Model Performance',color:'var(--red)',desc:'GPT-4 counts tokens for pricing & context limits. ~1 token ≈ 4 chars ≈ 0.75 words in English. Longer tokenizations = more context used = higher cost. Non-English languages often tokenize less efficiently — French, German can use 2-3x more tokens per word than English.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>

          {/* Pipeline visualization */}
          <Panel title="NLP PREPROCESSING PIPELINE" accent={ACCENT}>
            <div style={{display:'flex',gap:'4px',alignItems:'center',flexWrap:'wrap'}}>
              {[
                ['Raw Text','The quick Brown FOX jumps...','var(--t2)'],
                ['→','','var(--t3)'],
                ['Normalize','the quick brown fox jumps...','var(--cyan)'],
                ['→','','var(--t3)'],
                ['Tokenize','[the][quick][brown][fox]...','var(--gold)'],
                ['→','','var(--t3)'],
                ['Remove Stop','[quick][brown][fox][jumps]...','var(--green)'],
                ['→','','var(--t3)'],
                ['Stem/Lemma','[quick][brown][fox][jump]...','var(--orange)'],
                ['→','','var(--t3)'],
                ['Vectorize','[0.12,0.45,0.0,...]','var(--violet)'],
                ['→','','var(--t3)'],
                ['Model Input','✓ Ready','var(--green)'],
              ].map(([label,example,color],i)=>(
                <div key={i} style={{textAlign:'center'}}>
                  {label==='→'
                    ? <span style={{color:'var(--t3)',fontSize:'14px',padding:'0 4px'}}>→</span>
                    : <div style={{padding:'8px 10px',background:color+'18',border:`1px solid ${color}44`,borderRadius:'3px',minWidth:'90px'}}>
                        <div style={{fontSize:'9px',fontWeight:700,color,marginBottom:'4px',letterSpacing:'.05em'}}>{label}</div>
                        <div style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{example}</div>
                      </div>
                  }
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
